import getOr from 'lodash/get';
import isEmpty from 'lodash/fp/isEmpty';
import startCase from 'lodash/fp/startCase';
import { Moment } from 'moment';

import { FormDataType } from 'contexts/FormProvider';
import { formatDate, toNumber } from 'utils/utils';

import { ProductTypeList, ProductTypeListValues } from 'pages/ComplaintComplimentFeedback/ComplaintDetailsForm/enum';

import { remapFHA } from './remap_fha';

export const remap = (data: Partial<FormDataType>): object => {
  const get = (key: keyof FormDataType, defaultValue: string | null = null) => {
    if (isEmpty(data)) {
      return null;
    }
    return getOr(data, key, defaultValue);
  };

  const isIndividual = data.indOrBus === 'individual';
  const isComplaint = data.lodgement === 'Complaint';
  const isCompliment = data.lodgement === 'Compliment';
  const isFeedback = data.lodgement === 'Feedback';
  const isFHA = data.lodgement === 'Financial hardship application';

  if (isFHA) {
    return remapFHA(data);
  }

  const postalAddress =
    data.addressType === 'no'
      ? {
          country: get('posCountry'),
          street_address: get('posStreetAddress'),
          suburb: get('posSuburb'),
          state: get('posState'),
          postcode: get('posPostcode'),
        }
      : null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let contactReason: any = {
    policy_or_claim: data.policyOrClaim,
    claim_number: get('claimNumber'),
    policy_number: get('policyNumber'),
    individual_or_business: startCase(data.indOrBus as string),
    business_name: get('businessName'),
  };

  if (isComplaint) {
    contactReason = { ...contactReason, small_business: get('isSmallBusiness') };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let personalDetails: any = {
    title: data.title,
    first_name: data.firstname,
    last_name: data.lastname,
  };
  if (isComplaint) {
    personalDetails = {
      ...personalDetails,
      date_of_birth: isIndividual ? formatDate(data.dob as Date | Moment) : null,
      gender: get('gender'),
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let contactDetails: any = {};

  if (isComplaint) {
    contactDetails = {
      preferred_method_of_contact: data.iContactPreferedMethod,
      email_address: data.cEmailAddress,
      physical_address: {
        country: get('phyCountry'),
        street_address: get('phyStreetAddress'),
        suburb: get('phySuburb'),
        state: get('phyState'),
        postcode: get('phyPostcode'),
      },
      postal_address_is_same_as_physical_address: data.addressType,
      postal_address: postalAddress,
      mobile_phone: get('mPhoneNumber'),
      home_phone: get('hPhoneNumber'),
      other_phone: get('oPhoneNumber'),
      language_interpreter_required: data.isLangInterpreter,
      language: data.isLangInterpreter === 'yes' ? data.language : null,
      other_language: data.isLangInterpreter === 'yes' && data.language === 'Other' ? get('otherLanguage') : null,
    };
  }

  if (isCompliment || isFeedback) {
    contactDetails = {
      email_address: data.cEmailAddress,
      postal_address: {
        country: get('phyCountry'),
        street_address: get('phyStreetAddress'),
        suburb: get('phySuburb'),
        state: get('phyState'),
        postcode: get('phyPostcode'),
      },
      mobile_phone: get('mPhoneNumber'),
      home_phone: get('hPhoneNumber'),
      other_phone: get('oPhoneNumber'),
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let tellUsMore: any = {};

  const productIndex = ProductTypeList.findIndex((v) => v === data.productType);
  const product = ProductTypeListValues[productIndex];

  const compArea = data.compArea?.replace(/\//g, ''); // removing / (forward slash)

  if (isComplaint) {
    tellUsMore = {
      complaint_details: {
        product: data.productType,
        complaint_type: data.compType,
        complaint_category: data.compCategory,
        complaint_description: get('compDescription'),
        desired_outcome: get('desiredOutcome'),
        amount_in_dispute: toNumber(get('amountInDispute') as string),
      },
    };
  }

  if (isFeedback) {
    tellUsMore = {
      feedback_details: {
        product,
        feedback_type: startCase(data.compType as string),
        feedback_category: compArea,
        feedback_description: get('compDescription'),
      },
    };
  }

  if (isCompliment) {
    tellUsMore = {
      compliment_details: {
        product,
        compliment_type: startCase(data.compType as string),
        compliment_category: compArea,
        compliment_description: get('compDescription'),
      },
    };
  }

  return {
    your_details: {
      contact_reason: contactReason,
      personal_details: personalDetails,
      contact_details: contactDetails,
    },
    tell_us_more: tellUsMore,
  };
};
